import { AdminInput, Disclaimer, Div, P, Pill, lightTheme } from '@vgrubs/components';
import styled from 'styled-components';

/** Ratios supported by the AI image backend — px inputs map to the nearest one. */
const RATIOS = [
  '1:1',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
  '5:4',
  '4:3',
  '3:2',
  '16:9',
  '21:9',
];

/**
 * Flat value: `selection` is the active chip (`'auto'`, `'custom'`, or a ratio
 * string like `'16:9'`), plus the always-visible width/height in px. When a
 * ratio chip is selected the two dimensions are locked to it — editing one
 * recomputes the other; `'custom'` frees them; `'auto'` sends no size at all.
 */
export type AspectRatioValue = {
  selection: 'auto' | 'custom' | string;
  width?: number;
  height?: number;
};

export const DEFAULT_ASPECT_RATIO: AspectRatioValue = { selection: 'auto' };

const isRatio = (selection: string) =>
  selection !== 'auto' && selection !== 'custom';

const ratioParts = (r: string) => {
  const [w, h] = r.split(':').map(Number);
  return { w, h };
};

/**
 * Maps the picker value onto the DTO fields. Concrete width+height win (they
 * encode the ratio precisely and the backend maps them to the nearest supported
 * one); otherwise a selected ratio chip sends `aspectRatio`. Auto — or a custom
 * selection with incomplete dimensions — sends nothing.
 */
export const toAspectRatioDto = (
  v: AspectRatioValue
): { aspectRatio?: string; width?: number; height?: number } => {
  if (v.width && v.height)
    return { width: Math.round(v.width), height: Math.round(v.height) };
  if (isRatio(v.selection)) return { aspectRatio: v.selection };
  return {};
};

// Tiny rectangle whose shape mirrors the ratio (longest side 16px) — the
// Ideogram/Leonardo-style visual cue that makes ratios scannable at a glance.
const RatioShape = styled.span<{ $w: number; $h: number; $light?: boolean }>`
  flex: none;
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  border: 1.5px solid
    ${({ theme, $light }) => ($light ? theme.white : theme.darkGray)};
  border-radius: 3px;
`;

const shapeSize = (ratio: string) => {
  const { w, h } = ratioParts(ratio);
  return w >= h
    ? { w: 16, h: Math.round((16 * h) / w) }
    : { w: Math.round((16 * w) / h), h: 16 };
};

/**
 * Aspect-ratio selector for AI image generation: Auto / preset ratio chips /
 * Custom, with width & height (px) always visible below. Picking a ratio locks
 * the two dimensions to it; Custom frees them. Selected chip is filled with the
 * AI gradient — via inline `style` because Pill's `$background` feeds
 * `background-color`, which rejects gradients.
 */
export const AspectRatioPicker = ({
  value,
  onChange,
  disabled,
}: {
  value: AspectRatioValue;
  onChange: (value: AspectRatioValue) => void;
  disabled?: boolean;
}) => {
  const { selection, width, height } = value;

  // Selecting a ratio locks the dimensions to it: keep whichever side is already
  // filled and recompute the other so the pair matches the new ratio.
  const selectRatio = (r: string) => {
    const { w, h } = ratioParts(r);
    if (width != null)
      onChange({ selection: r, width, height: Math.round((width * h) / w) });
    else if (height != null)
      onChange({ selection: r, width: Math.round((height * w) / h), height });
    else onChange({ selection: r, width, height });
  };

  const selectChip = (key: string) => {
    if (key === 'auto') onChange({ selection: 'auto' }); // no size preference
    else if (key === 'custom') onChange({ selection: 'custom', width, height });
    else selectRatio(key);
  };

  // Editing a dimension. Under a ratio, the other side is recomputed to keep the
  // ratio; otherwise the two are independent, and typing under Auto flips the
  // selection to Custom (a concrete size is no longer "let the AI decide").
  const editDimension = (edited: 'width' | 'height', raw: string) => {
    const n = raw ? Number(raw) : undefined;
    if (isRatio(selection)) {
      const { w, h } = ratioParts(selection);
      if (edited === 'width')
        onChange({
          selection,
          width: n,
          height: n != null ? Math.round((n * h) / w) : height,
        });
      else
        onChange({
          selection,
          width: n != null ? Math.round((n * w) / h) : width,
          height: n,
        });
      return;
    }
    const nextSelection = selection === 'auto' ? 'custom' : selection;
    onChange({
      selection: nextSelection,
      width: edited === 'width' ? n : width,
      height: edited === 'height' ? n : height,
    });
  };

  const chips: { key: string; label: string; ratio?: string }[] = [
    { key: 'auto', label: 'Auto' },
    ...RATIOS.map((r) => ({ key: r, label: r, ratio: r })),
    { key: 'custom', label: 'Custom' },
  ];

  const hint = isRatio(selection)
    ? `Width and height stay locked to the ${selection} ratio.`
    : selection === 'custom'
    ? 'The closest supported ratio to these dimensions is used.'
    : 'Auto lets the AI choose the ratio. Enter width or height to set your own.';

  return (
    <div>
      <P $mb="8">Aspect ratio</P>
      <Div
        $flex
        $gap="8px"
        style={{
          flexWrap: 'wrap',
          ...(disabled ? { opacity: 0.6, pointerEvents: 'none' } : {}),
        }}
      >
        {chips.map(({ key, label, ratio }) => {
          const selected = selection === key;
          const shape = ratio ? shapeSize(ratio) : null;
          return (
            <Pill
              key={key}
              $pointer
              $minWidth="0"
              $height="32px"
              $padding="4px 12px"
              $gap="6px"
              $background="none"
              $border={selected ? 'transparent' : lightTheme.lightGray}
              style={selected ? { background: lightTheme.aiGradient } : undefined}
              onClick={() => selectChip(key)}
            >
              {shape && <RatioShape $w={shape.w} $h={shape.h} $light={selected} />}
              <Disclaimer
                $m="0"
                $color={selected ? lightTheme.white : lightTheme.darkGray}
              >
                {label}
              </Disclaimer>
            </Pill>
          );
        })}
      </Div>

      <Div
        $flex
        $gap="16px"
        $mt="12"
        style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
      >
        <AdminInput
          type="number"
          min={1}
          label="Width"
          suffix="px"
          placeholder="e.g. 1024"
          value={width ?? ''}
          onChange={({ target }) => editDimension('width', target.value)}
          disabled={disabled}
        />
        <AdminInput
          type="number"
          min={1}
          label="Height"
          suffix="px"
          placeholder="e.g. 1024"
          value={height ?? ''}
          onChange={({ target }) => editDimension('height', target.value)}
          disabled={disabled}
        />
      </Div>
      <Disclaimer $m="0" $mt="8" $color={lightTheme.lightGray}>
        {hint}
      </Disclaimer>
    </div>
  );
};
